// Shared VirtualID center-logo overlay for canvas-rendered QR codes.
//
// Both the TOTP QR (settings.ts) and the share-link QR (links.ts) render QR
// modules at HIGH error-correction (30% recovery), then call
// `drawQrCenterLogo(ctx, px)` to stamp the VirtualID mark in the middle.
//
// Sizing rationale: the logo box is ~18% of the QR width. A center overlay
// larger than ~22-25% breaks scannability even at HIGH ECC, so 18% (plus an
// 18%-larger white knockout, ~21% total footprint) stays comfortably within
// the recovery budget while remaining clearly visible.
//
// The mark is drawn entirely with canvas primitives (no async Image() load), so
// the QR renderers stay synchronous and race-free.

// Rounded-rectangle path helper (device px).
function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

// Draw the VirtualID app-icon mark centered at (cx, cy) filling a boxSize
// square: an indigo (#4f46e5) rounded square with a white ID-card glyph.
// The glyph is authored in a 0..24 viewBox and mapped into the box.
export function drawVidLogo(ctx: CanvasRenderingContext2D, cx: number, cy: number, boxSize: number): void {
  const scale = boxSize / 24;
  const x0 = cx - boxSize / 2;
  const y0 = cy - boxSize / 2;

  ctx.save();

  // Indigo app-icon body (rounded square, ~22% corner radius like the SVG).
  ctx.fillStyle = '#4f46e5';
  roundRectPath(ctx, x0, y0, boxSize, boxSize, boxSize * 0.22);
  ctx.fill();

  // Map 0..24 glyph space into the box.
  ctx.translate(x0, y0);
  ctx.scale(scale, scale);

  ctx.strokeStyle = '#fff';
  ctx.fillStyle = '#fff';
  ctx.lineWidth = 1.8;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // ID-card outline: rect(3,4,18,16, rx3).
  roundRectPath(ctx, 3, 4, 18, 16, 3);
  ctx.stroke();

  // Portrait: circle(9,11, r2.4).
  ctx.beginPath();
  ctx.arc(9, 11, 2.4, 0, Math.PI * 2);
  ctx.stroke();

  // Text lines: M14 9 h4 / M14 13 h4.
  ctx.beginPath();
  ctx.moveTo(14, 9); ctx.lineTo(18, 9);
  ctx.moveTo(14, 13); ctx.lineTo(18, 13);
  ctx.stroke();

  // Smile under the portrait: M6 16.5 c .8 -1.6 4.2 -1.6 5 0.
  ctx.beginPath();
  ctx.moveTo(6, 16.5);
  ctx.bezierCurveTo(6.8, 14.9, 10.2, 14.9, 11, 16.5);
  ctx.stroke();

  ctx.restore();
}

// Stamp the VirtualID logo in the center of an already-drawn QR canvas.
// `px` is the canvas pixel width (square). Draws a white rounded-square
// knockout (~18% larger than the logo) so the mark sits on clean white and
// doesn't merge with dark modules, then the logo itself at ~18% of the QR.
export function drawQrCenterLogo(ctx: CanvasRenderingContext2D, px: number): void {
  const logoPx = Math.round(px * 0.18);
  const knockoutPx = Math.round(logoPx * 1.18);
  const cx = px / 2;
  const cy = px / 2;

  ctx.save();
  // White knockout.
  ctx.fillStyle = '#fff';
  roundRectPath(ctx, cx - knockoutPx / 2, cy - knockoutPx / 2, knockoutPx, knockoutPx, knockoutPx * 0.22);
  ctx.fill();
  ctx.restore();

  // Indigo VirtualID mark.
  drawVidLogo(ctx, cx, cy, logoPx);
}
